import {
  RecordingConfigPublic,
  GetBehaviorSignalSettingsResponse,
  BehaviorSignalSettings,
  BehaviorSignalElementBlock,
  UpdateBehaviorSignalSettingsResponse,
  UpdateRecordingFeaturesPublicRequest,
  GetKeepRulesResponse,
  UpdateBehaviorSignalSettingsRequest,
  BehaviorSignalElementBlockUpdate,
  DefaultPrivacyMode,
  DetectionsConfigurationRules,
} from '@fullstory/proto/fs/services/ingestion-settings';
import { createGRPCAction, RequestActionType } from '@fullstory/redux-util';
import { IngestionSettingsTypes, BehaviorSignalTypeArray, KeepRuleSetWithId } from './types';
import { without } from 'lodash';
import { UID } from '@fullstory/util/uid';
import { int64 } from '@fullstory/proto/google/protobuf';

export const IngestionSettingsRequestActions = {
  getBehaviorSignalSettings: (orgId: string) =>
    createGRPCAction<
      typeof RecordingConfigPublic.GetBehaviorSignalSettings,
      BehaviorSignalSettings | null
    >({
      modify: (rsp: GetBehaviorSignalSettingsResponse): BehaviorSignalSettings | null =>
        rsp.versions[0] ?? null,
    })(
      {
        type: IngestionSettingsTypes.FetchBehaviorSignalSettingsRequest,
        grpcFunction: RecordingConfigPublic.GetBehaviorSignalSettings,
        body: { orgId, mobile: false, version: int64.zero, limit: 1 },
        readWrite: 'write',
        force: true,
      },
      IngestionSettingsTypes.FetchBehaviorSignalSettingsSuccess,
      IngestionSettingsTypes.FetchBehaviorSignalSettingsFailure,
    ),
  createBehaviorSignalSetting: (orgId: string, elementBlock: BehaviorSignalElementBlock) =>
    createGRPCAction<
      typeof RecordingConfigPublic.UpdateBehaviorSignalSettings,
      BehaviorSignalSettings | null
    >({
      modify: (rsp: UpdateBehaviorSignalSettingsResponse): BehaviorSignalSettings | null =>
        rsp.behaviorSignalSettings ?? null,
    })(
      {
        type: IngestionSettingsTypes.CreateBehaviorSignalSettingsRequest,
        grpcFunction: RecordingConfigPublic.UpdateBehaviorSignalSettings,
        body: {
          orgId,
          elementBlockAdds: [elementBlock],
          elementBlockDeletes: [],
          elementBlockDeletesByNamedElementId: [],
          elementBlockUpdates: {},
          elementBlockUpdatesByNamedElementId: {},
        },
        readWrite: 'write',
        force: true,
      },
      IngestionSettingsTypes.CreateBehaviorSignalSettingsSuccess,
      IngestionSettingsTypes.CreateBehaviorSignalSettingsFailure,
    ),
  deleteBehaviorSignalSettingBySelector: (orgId: string, elementBlockSelector: string) =>
    createGRPCAction<
      typeof RecordingConfigPublic.UpdateBehaviorSignalSettings,
      BehaviorSignalSettings | null
    >({
      modify: (rsp: UpdateBehaviorSignalSettingsResponse): BehaviorSignalSettings | null =>
        rsp.behaviorSignalSettings ?? null,
    })(
      {
        type: IngestionSettingsTypes.DeleteBehaviorSignalSettingsRequest,
        grpcFunction: RecordingConfigPublic.UpdateBehaviorSignalSettings,
        body: {
          orgId,
          elementBlockAdds: [],
          elementBlockDeletes: [elementBlockSelector],
          elementBlockDeletesByNamedElementId: [],
          elementBlockUpdates: {},
          elementBlockUpdatesByNamedElementId: {},
        },
        readWrite: 'write',
        force: true,
      },
      IngestionSettingsTypes.DeleteBehaviorSignalSettingsSuccess,
      IngestionSettingsTypes.DeleteBehaviorSignalSettingsFailure,
    ),
  deleteBehaviorSignalSettingByNamedElement: (orgId: string, namedElementId: string) =>
    createGRPCAction<
      typeof RecordingConfigPublic.UpdateBehaviorSignalSettings,
      BehaviorSignalSettings | null
    >({
      modify: (rsp: UpdateBehaviorSignalSettingsResponse): BehaviorSignalSettings | null =>
        rsp.behaviorSignalSettings ?? null,
    })(
      {
        type: IngestionSettingsTypes.DeleteBehaviorSignalSettingsRequest,
        grpcFunction: RecordingConfigPublic.UpdateBehaviorSignalSettings,
        body: {
          orgId,
          elementBlockAdds: [],
          elementBlockDeletes: [],
          elementBlockDeletesByNamedElementId: [namedElementId],
          elementBlockUpdates: {},
          elementBlockUpdatesByNamedElementId: {},
        },
        readWrite: 'write',
        force: true,
      },
      IngestionSettingsTypes.DeleteBehaviorSignalSettingsSuccess,
      IngestionSettingsTypes.DeleteBehaviorSignalSettingsFailure,
    ),
  updateBehaviorSignalSetting: (
    orgId: string,
    elementBlock: BehaviorSignalElementBlock,
    prevElementBlock: BehaviorSignalElementBlock,
  ) => {
    const update: BehaviorSignalElementBlockUpdate = {
      // only one of namedElementId and selector should be non empty
      // otherwise will fail on server
      namedElementId: elementBlock.namedElementId,
      selector: elementBlock.selector,
      addSignals: elementBlock.signals,
      removeSignals: without(BehaviorSignalTypeArray, ...elementBlock.signals),
      notes: elementBlock.notes,
    };
    const body: UpdateBehaviorSignalSettingsRequest = {
      orgId,
      elementBlockAdds: [],
      elementBlockDeletes: [],
      elementBlockDeletesByNamedElementId: [],
      elementBlockUpdates: {},
      elementBlockUpdatesByNamedElementId: {},
    };
    if (prevElementBlock.namedElementId) {
      body.elementBlockUpdatesByNamedElementId[prevElementBlock.namedElementId] = update;
    } else {
      body.elementBlockUpdates[prevElementBlock.selector] = update;
    }
    return createGRPCAction<
      typeof RecordingConfigPublic.UpdateBehaviorSignalSettings,
      BehaviorSignalSettings | null
    >({
      modify: (rsp: UpdateBehaviorSignalSettingsResponse): BehaviorSignalSettings | null =>
        rsp.behaviorSignalSettings ?? null,
    })(
      {
        type: IngestionSettingsTypes.UpdateBehaviorSignalSettingsRequest,
        grpcFunction: RecordingConfigPublic.UpdateBehaviorSignalSettings,
        body,
        readWrite: 'write',
        force: true,
      },
      IngestionSettingsTypes.UpdateBehaviorSignalSettingsSuccess,
      IngestionSettingsTypes.UpdateBehaviorSignalSettingsFailure,
    );
  },
  getDetectionsConfigRules: (orgId: string, force: boolean) =>
    createGRPCAction<typeof RecordingConfigPublic.GetDetectionsConfigurationRules>()(
      {
        type: IngestionSettingsTypes.FetchDetectionsConfigRulesRequest,
        grpcFunction: RecordingConfigPublic.GetDetectionsConfigurationRules,
        body: { orgId, mobile: false, version: int64.zero, limit: 1 },
        force,
        readWrite: 'read',
      },
      IngestionSettingsTypes.FetchDetectionsConfigRulesSuccess,
      IngestionSettingsTypes.FetchDetectionsConfigRulesFailure,
    ),
  updateDetectionsConfigRules: (
    orgId: string,
    detectionsConfigRules: DetectionsConfigurationRules,
  ) =>
    createGRPCAction<typeof RecordingConfigPublic.UpdateDetectionsConfigurationRules>()(
      {
        type: IngestionSettingsTypes.UpdateDetectionsConfigRulesRequest,
        grpcFunction: RecordingConfigPublic.UpdateDetectionsConfigurationRules,
        body: { orgId, detectionsConfigRules },
        force: true,
        readWrite: 'write',
      },
      IngestionSettingsTypes.UpdateDetectionsConfigRulesSuccess,
      IngestionSettingsTypes.UpdateDetectionsConfigRulesFailure,
    ),
  getRecordingFeaturesPublic: (orgId: string) =>
    createGRPCAction<typeof RecordingConfigPublic.GetRecordingFeatures>()(
      {
        type: IngestionSettingsTypes.FetchRecordingFeaturesRequest,
        grpcFunction: RecordingConfigPublic.GetRecordingFeatures,
        body: { orgId, mobile: false, version: int64.zero, limit: 1 },
        force: true,
        readWrite: 'write',
      },
      IngestionSettingsTypes.FetchRecordingFeaturesSuccess,
      IngestionSettingsTypes.FetchRecordingFeaturesFailure,
    ),
  updateClientSideRageClick: (orgId: string, clientSideRageClick: boolean) =>
    createGRPCAction<
      typeof RecordingConfigPublic.UpdateRecordingFeaturesPublic,
      Partial<UpdateRecordingFeaturesPublicRequest>
    >({
      modify: () => ({ clientSideRageClick }),
    })(
      {
        type: IngestionSettingsTypes.UpdateClientSideRageClickRequest,
        grpcFunction: RecordingConfigPublic.UpdateRecordingFeaturesPublic,
        body: {
          orgId,
          clientSideRageClick,
        },
        readWrite: 'write',
        force: true,
      },
      IngestionSettingsTypes.UpdateClientSideRageClickSuccess,
      IngestionSettingsTypes.UpdateClientSideRageClickFailure,
    ),
  getKeepRules: (orgId: string) =>
    createGRPCAction<typeof RecordingConfigPublic.GetKeepRules, KeepRuleSetWithId | null>({
      modify: (rsp: GetKeepRulesResponse): KeepRuleSetWithId | null =>
        rsp.versions[0]
          ? {
              ...rsp.versions[0],
              rules: rsp.versions[0].rules.map(rule => ({ ...rule, id: UID() })),
            }
          : null,
    })(
      {
        type: IngestionSettingsTypes.FetchKeepRulesRequest,
        grpcFunction: RecordingConfigPublic.GetKeepRules,
        body: { orgId, mobile: false, version: int64.zero, limit: 1 },
        readWrite: 'write',
      },
      IngestionSettingsTypes.FetchKeepRulesSuccess,
      IngestionSettingsTypes.FetchKeepRulesFailure,
    ),
  validateBlockedIpRule: (orgId: string, rules: string[]) =>
    createGRPCAction<typeof RecordingConfigPublic.ValidateBlockedIpRule>()(
      {
        type: IngestionSettingsTypes.ValidateBlockedIpRuleRequest,
        grpcFunction: RecordingConfigPublic.ValidateBlockedIpRule,
        body: { orgId, blockedIpRule: rules },
        readWrite: 'write',
      },
      IngestionSettingsTypes.ValidateBlockedIpRuleSuccess,
      IngestionSettingsTypes.ValidateBlockedIpRuleFailure,
    ),
  updateDefaultPrivacyMode: (orgId: string, newPrivacyMode: DefaultPrivacyMode) =>
    createGRPCAction<typeof RecordingConfigPublic.UpdateDefaultPrivacyMode, {}>({
      modify: () => ({ newPrivacyMode }),
    })(
      {
        type: IngestionSettingsTypes.UpdateDefaultPrivacyModeRpcRequest,
        grpcFunction: RecordingConfigPublic.UpdateDefaultPrivacyMode,
        body: { orgId, newPrivacyMode },
        readWrite: 'write',
      },
      IngestionSettingsTypes.UpdateDefaultPrivacyModeRpcSuccess,
      IngestionSettingsTypes.UpdateDefaultPrivacyModeRpcFailure,
    ),
};

export type IngestionSettingsActions = RequestActionType<typeof IngestionSettingsRequestActions>;
